import cn from "classnames"
import { Field, FieldProps } from "formik"
import React, { ChangeEvent } from "react"
import { useTranslation } from "react-i18next"

import { getRequiredMessage } from "~/components/Form/helpers"

import imgClear from "./assets/clear.svg"
import styles from "./index.module.css"

type InputProps = JSX.IntrinsicElements["input"] & {
  label: string
  type?: string
  active?: boolean
  error?: boolean
  errorMessage?: string
  isActive?: boolean
  isRequired?: boolean
  onChange: (e: ChangeEvent<HTMLInputElement>) => void
  onClear?: () => void
}

export const Input: React.FC<InputProps> = ({
  className,
  label,
  type = "text",
  error,
  errorMessage,
  value,
  defaultValue,
  isRequired,
  onChange,
  onClear,
  ...properties
}) => (
  <div
    className={cn(styles.Wrapper, className, {
      [styles.WithClearBtn]: onClear,
    })}
  >
    <div className={styles.Field}>
      <input
        className={cn({
          [styles._active]: !!value || !!defaultValue,
          [styles._error]: error,
        })}
        defaultValue={defaultValue}
        id={label}
        type={type}
        value={value}
        onChange={onChange}
        {...properties}
      />

      {onClear && value !== "" && (
        <div className={styles.Clear} onClick={onClear}>
          <img src={imgClear} />
        </div>
      )}

      <label htmlFor={label}>{isRequired ? `${label} *` : label}</label>
    </div>

    {errorMessage && <div className={styles.ErrorMessage}>{errorMessage}</div>}
  </div>
)

type FormInput = {
  label: string
  type?: string
  active?: boolean
  className?: string
  name: string
  disabled?: boolean
  isRequired?: boolean
}

export const FormInput: React.FC<FormInput> = ({
  className,
  label,
  type = "text",
  name,
  isRequired,
  ...properties
}) => {
  const { t } = useTranslation()

  return (
    <Field name={name}>
      {({ field, meta }: FieldProps) => (
        <Input
          className={className}
          error={!!meta.error && meta.touched}
          errorMessage={getRequiredMessage(t, meta, isRequired)}
          isRequired={isRequired}
          label={label}
          type={type}
          value={meta.value}
          {...field}
          {...properties}
        />
      )}
    </Field>
  )
}
